/**
 * Risk Tab - Portfolio risk analysis
 * Now using reusable components for better maintainability
 * Enhanced with Risk Trend Chart, Risk Contributors, and What-If Simulator
 */

import React, { useState, useMemo } from 'react';
import { AlertTriangle, Globe } from 'lucide-react';
import {
  RiskMetrics,
  StressTests,
  CorrelationMatrix,
  VarDistribution
} from '../../services/api/portfolioApiService';
import {
  RiskMetricCard,
  StressTestResults,
  LoadingSpinner,
  ActionCard
} from './index';
import RiskTrendChart, { TrendDataPoint } from './molecules/RiskTrendChart';
import RiskContributorsTable from './molecules/RiskContributorsTable';
import WhatIfSimulator from './molecules/WhatIfSimulator';
import DrawdownAnalysisSection from './molecules/DrawdownAnalysisSection';
import PortfolioComposer from './PortfolioComposer';
import ProposalsList from './ProposalsList';

interface RiskTabProps {
  mode: 'global' | 'client';
  riskMetrics: RiskMetrics | null;
  stressTests: StressTests | null;
  correlationMatrix?: CorrelationMatrix | null;
  varDistribution?: VarDistribution[];
  positions?: any[];
  portfolioValue?: number;
  performanceHistory?: any[];
  performanceMetrics?: { max_drawdown?: number | null; current_drawdown?: number | null } | null;
  loading?: boolean;
  clientId?: string; // Required for PortfolioComposer
  portfolioId?: string; // Filter simulate endpoint to specific portfolio
}

const RiskTab: React.FC<RiskTabProps> = ({
  mode: _mode,
  riskMetrics,
  stressTests,  // TASK-281: Use real stress tests from API
  correlationMatrix: _correlationMatrix,
  varDistribution: _varDistribution,
  positions,
  portfolioValue = 0,
  performanceHistory,
  performanceMetrics,
  loading = false,
  clientId,
  portfolioId
}) => {
  // ===== ALL HOOKS MUST BE AT THE TOP (before any conditional returns) =====
  const [showPortfolioComposer, setShowPortfolioComposer] = useState(false);
  const [proposalsRefreshTrigger, setProposalsRefreshTrigger] = useState(0);

  // TASK-281: NO MOCK FALLBACK - use real positions only
  const effectivePositions = positions || [];
  const calculatedValue = effectivePositions.reduce((sum, p) => sum + (p.market_value || 0), 0);

  // TASK-281: Calculate effective portfolio value from real data only
  const effectivePortfolioValue = portfolioValue && portfolioValue > 0
    ? portfolioValue
    : calculatedValue > 0
      ? calculatedValue
      : 0;

  // TASK-281: NO MOCK FALLBACK - use real risk metrics only
  // TASK-582: prefer the backend's honest data_quality.sufficient when present. The old
  // heuristic mistook a legitimately-computed beta ≈ 1.0 for "no data"; with real betas now
  // possible that sentinel is unsafe, so it is only the fallback for payloads lacking data_quality.
  const hasValidRiskMetrics = !!riskMetrics && (
    riskMetrics.data_quality
      ? riskMetrics.data_quality.sufficient
      : ((riskMetrics.volatility || 0) > 0 ||
         ((riskMetrics.beta || 0) > 0 && (riskMetrics.beta || 0) !== 1) ||
         (riskMetrics.sharpe_ratio || 0) > 0 ||
         ((riskMetrics.var_95 || 0) !== 0) ||
         ((riskMetrics.var_1day || 0) !== 0)));

  // TASK-281: Use real metrics with safe defaults for null access
  // Default empty metrics to prevent null access errors
  const defaultRiskMetrics: RiskMetrics = {
    volatility: 0,
    var_1day: 0,
    var_10day: 0,
    var_95: 0,
    beta: 1,
    concentration_risk: 0,
    sharpe_ratio: 0,
    expected_shortfall: 0,
  };
  // Normalise nulls to numbers for the arithmetic/formatting below. The honest "no data" state
  // is conveyed by hasValidRiskMetrics, not by pretending these numbers are real.
  const _rawRiskMetrics = hasValidRiskMetrics && riskMetrics ? riskMetrics : defaultRiskMetrics;
  const effectiveRiskMetrics = {
    ..._rawRiskMetrics,
    volatility: _rawRiskMetrics.volatility ?? 0,
    var_1day: _rawRiskMetrics.var_1day ?? 0,
    var_10day: _rawRiskMetrics.var_10day ?? 0,
    var_95: _rawRiskMetrics.var_95 ?? 0,
    beta: _rawRiskMetrics.beta ?? 1,
    sharpe_ratio: _rawRiskMetrics.sharpe_ratio ?? 0,
    expected_shortfall: _rawRiskMetrics.expected_shortfall ?? 0,
  };

  // TASK-281: NO MOCK FALLBACK - transform API stress tests to component format
  // StressTests from API is { [scenario: string]: number }
  // StressTestScenario expects { name, impact, probability?, description? }
  const stressTestScenarios: import('./molecules/StressTestResults').StressTestScenario[] =
    React.useMemo(() => {
      if (!stressTests) return [];
      return Object.entries(stressTests).map(([name, impact]) => ({
        name,
        impact: typeof impact === 'number' ? impact : 0,
      }));
    }, [stressTests]);

  // Drawdown underwater data derived from performance history
  const underwaterData = React.useMemo(() => {
    if (!performanceHistory || performanceHistory.length === 0) return [];
    return performanceHistory.map((d: any, i: number, arr: any[]) => {
      const peak = Math.max(...arr.slice(0, i + 1).map((p: any) => p.portfolio_value || p.value || 0));
      const value = d.portfolio_value || d.value || 0;
      const drawdown = peak > 0 ? ((value - peak) / peak) * 100 : 0;
      return { date: d.date, drawdown };
    });
  }, [performanceHistory]);

  // Derive historical risk trend from performance history using rolling 20-day volatility
  const riskTrendData: TrendDataPoint[] = useMemo(() => {
    if (!performanceHistory || performanceHistory.length < 22) return [];

    const WINDOW = 20; // 20 trading days ≈ 1 month
    const ANNUALIZE = Math.sqrt(252);
    const points: TrendDataPoint[] = [];

    // Extract daily returns from the history.
    // `value` is cumulative % return from start (e.g. 2.5 = +2.5%).
    // Convert to indexed series (base 100) before computing daily returns.
    const dailyReturns: { date: string; ret: number }[] = [];
    for (let i = 1; i < performanceHistory.length; i++) {
      const prevRaw = performanceHistory[i - 1].portfolio_value;
      const currRaw = performanceHistory[i].portfolio_value;
      let prev: number, curr: number;
      if (prevRaw != null && currRaw != null) {
        prev = Number(prevRaw);
        curr = Number(currRaw);
      } else {
        prev = 100 + (performanceHistory[i - 1].value || 0);
        curr = 100 + (performanceHistory[i].value || 0);
      }
      if (prev > 0) {
        dailyReturns.push({
          date: performanceHistory[i].date,
          ret: (curr - prev) / prev,
        });
      }
    }

    // Compute rolling volatility every 5 days to avoid too many points
    for (let i = WINDOW; i < dailyReturns.length; i += 5) {
      const window = dailyReturns.slice(i - WINDOW, i);
      const mean = window.reduce((s, d) => s + d.ret, 0) / window.length;
      const variance = window.reduce((s, d) => s + (d.ret - mean) ** 2, 0) / (window.length - 1);
      const vol = Math.sqrt(variance) * ANNUALIZE * 100; // annualized %
      const var95 = effectivePortfolioValue * (vol / 100) * 1.645 / ANNUALIZE;

      points.push({
        date: dailyReturns[i].date.slice(0, 10),
        volatility: Number(vol.toFixed(1)),
        var: Number(var95.toFixed(0)),
      });
    }

    return points;
  }, [performanceHistory, effectivePortfolioValue]);

  // Determine portfolio base currency (default CHF)
  const baseCurrency = useMemo(() => {
    const first = effectivePositions.find((p: any) => p.fx_base_currency);
    return first?.fx_base_currency || 'CHF';
  }, [effectivePositions]);

  // FX Exposure: group positions by local currency
  const fxExposure = useMemo(() => {
    if (effectivePositions.length === 0 || effectivePortfolioValue <= 0) return [];
    const currencyMap = new Map<string, { totalMv: number; fxRateSum: number; count: number }>();

    for (const pos of effectivePositions) {
      const ccy = pos.fx_local_currency || pos.currency || baseCurrency;
      if (ccy === baseCurrency) continue; // skip base currency positions
      const entry = currencyMap.get(ccy) || { totalMv: 0, fxRateSum: 0, count: 0 };
      entry.totalMv += pos.market_value || 0;
      entry.fxRateSum += pos.fx_rate_local_to_base || 0;
      entry.count += 1;
      currencyMap.set(ccy, entry);
    }

    return Array.from(currencyMap.entries())
      .map(([currency, data]) => ({
        currency,
        exposure: data.totalMv,
        avgFxRate: data.count > 0 ? data.fxRateSum / data.count : 0,
        pctOfPortfolio: (data.totalMv / effectivePortfolioValue) * 100,
      }))
      .sort((a, b) => b.exposure - a.exposure);
  }, [effectivePositions, effectivePortfolioValue, baseCurrency]);

  // Geographic concentration: group by country
  const geoConcentration = useMemo(() => {
    if (effectivePositions.length === 0 || effectivePortfolioValue <= 0) return [];
    const countryMap = new Map<string, number>();

    for (const pos of effectivePositions) {
      const country = pos.country || 'Unknown';
      countryMap.set(country, (countryMap.get(country) || 0) + (pos.market_value || 0));
    }

    return Array.from(countryMap.entries())
      .map(([country, mv]) => ({
        country,
        marketValue: mv,
        weight: (mv / effectivePortfolioValue) * 100,
      }))
      .sort((a, b) => b.weight - a.weight);
  }, [effectivePositions, effectivePortfolioValue]);

  const concentratedCountries = geoConcentration.filter(c => c.weight > 40);

  // ===== ALL HOOKS ABOVE THIS LINE =====

  // Loading state
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6">
        <LoadingSpinner size="lg" text="Loading risk analysis..." />
      </div>
    );
  }

  // No data state - component will show default values (zeros)
  // This is intentional - we want to show the UI structure even without data
  // No data state - component will show default values (zeros)

  return (
    <div className="space-y-6">
      {/* Row 1: Key Risk Metrics - 4 Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <RiskMetricCard
          metric="Volatility"
          value={effectiveRiskMetrics.volatility || 0}
          description="Annual standard deviation"
          format="percentage"
          threshold={20}
          status={
            (effectiveRiskMetrics.volatility || 0) < 10 ? 'safe' :
            (effectiveRiskMetrics.volatility || 0) < 20 ? 'warning' : 'critical'
          }
        />

        <RiskMetricCard
          metric="Beta"
          value={effectiveRiskMetrics.beta || 1}
          description="Market sensitivity"
          format="number"
          threshold={1.2}
          status={
            Math.abs((effectiveRiskMetrics.beta || 1) - 1) < 0.2 ? 'safe' :
            Math.abs((effectiveRiskMetrics.beta || 1) - 1) < 0.5 ? 'warning' : 'critical'
          }
        />

        {/* TASK-872: `higherIsBetter` because this threshold is a FLOOR — a Sharpe of 0.4 is
            the weak side of 1, not the safe side. Every other card here states a ceiling, so
            without this the one metric where more is better would colour backwards. */}
        <RiskMetricCard
          metric="Sharpe Ratio"
          value={effectiveRiskMetrics.sharpe_ratio || 0}
          description="Risk-adjusted return"
          format="number"
          threshold={1}
          higherIsBetter
          status={
            (effectiveRiskMetrics.sharpe_ratio || 0) >= 1 ? 'safe' :
            (effectiveRiskMetrics.sharpe_ratio || 0) >= 0.5 ? 'warning' : 'critical'
          }
        />

        <RiskMetricCard
          metric="VaR (95%)"
          value={Math.abs(effectiveRiskMetrics.var_95 || 0)}
          description="5% chance of losing more"
          format="currency"
          currency="CHF"
          threshold={100000}
          status={
            Math.abs(effectiveRiskMetrics.var_95 || 0) < 50000 ? 'safe' :
            Math.abs(effectiveRiskMetrics.var_95 || 0) < 100000 ? 'warning' : 'critical'
          }
        />
      </div>

      {/* Row 2: Risk Trend Chart */}
      <RiskTrendChart
        currentVolatility={effectiveRiskMetrics.volatility || 0}
        currentVaR={Math.abs(effectiveRiskMetrics.var_95 || effectiveRiskMetrics.var_1day || 0)}
        currency="CHF"
        historicalData={riskTrendData.length > 0 ? riskTrendData : undefined}
      />

      {/* Row 3: Top Risk Contributors - Full Width - Always show with mock data if needed */}
      <RiskContributorsTable
        positions={effectivePositions}
        totalValue={effectivePortfolioValue}
        currency="CHF"
      />

      {/* Row 3b: FX Exposure */}
      {fxExposure.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-2 mb-4">
            <Globe className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">FX Exposure</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-3 text-gray-600 font-medium">Currency</th>
                  <th className="text-right py-2 px-3 text-gray-600 font-medium">Exposure (CHF)</th>
                  <th className="text-right py-2 px-3 text-gray-600 font-medium">FX Rate</th>
                  <th className="text-right py-2 px-3 text-gray-600 font-medium">% of Portfolio</th>
                </tr>
              </thead>
              <tbody>
                {fxExposure.map((fx) => (
                  <tr key={fx.currency} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-2 px-3 font-medium text-gray-900">{fx.currency}</td>
                    <td className="py-2 px-3 text-right text-gray-700">
                      {new Intl.NumberFormat('de-CH', { style: 'currency', currency: 'CHF', maximumFractionDigits: 0 }).format(fx.exposure)}
                    </td>
                    <td className="py-2 px-3 text-right text-gray-700">
                      {fx.avgFxRate > 0 ? fx.avgFxRate.toFixed(4) : '-'}
                    </td>
                    <td className="py-2 px-3 text-right text-gray-700">
                      {fx.pctOfPortfolio.toFixed(1)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Row 3c: Geographic Concentration */}
      {geoConcentration.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Geographic Concentration</h3>

          {/* If all positions lack country data, show info message instead of false warning */}
          {geoConcentration.length === 1 && geoConcentration[0].country === 'Unknown' ? (
            <div className="flex items-start gap-3 bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
              <Globe className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Geographic data not available
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Country classification is missing for the positions in this portfolio. Re-sync from your custodian to populate geographic data.
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Concentration Warning - only for real country data */}
              {concentratedCountries.filter(c => c.country !== 'Unknown').length > 0 && (
                <div className="mb-4 space-y-2">
                  {concentratedCountries.filter(c => c.country !== 'Unknown').map((c) => (
                    <div
                      key={c.country}
                      className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4"
                    >
                      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-amber-800">
                          {c.country}: {c.weight.toFixed(1)}% of portfolio
                        </p>
                        <p className="text-xs text-amber-700 mt-0.5">
                          Consider diversifying — single-country exposure above 40% threshold
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Country weight bars - only show if we have real country data */}
          {!(geoConcentration.length === 1 && geoConcentration[0].country === 'Unknown') && (
            <div className="space-y-2">
              {geoConcentration.map((c) => (
                <div key={c.country} className="flex items-center gap-3">
                  <span className="text-sm text-gray-700 w-28 truncate" title={c.country}>
                    {c.country}
                  </span>
                  <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                    <div
                      className={`h-full rounded-full ${
                        c.country === 'Unknown' ? 'bg-gray-300' :
                        c.weight > 40 ? 'bg-amber-500' : c.weight > 25 ? 'bg-blue-400' : 'bg-blue-300'
                      }`}
                      style={{ width: `${Math.min(c.weight, 100)}%` }}
                    />
                  </div>
                  <span className="text-sm text-gray-600 w-14 text-right">
                    {c.weight.toFixed(1)}%
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Row 4: Stress Tests + What-If Simulator Side-by-Side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Stress Test Scenarios - Always show with mock data */}
        <div className="bg-white rounded-lg shadow p-6">
          <StressTestResults
            scenarios={stressTestScenarios}
            title="Stress Test Scenarios"
            portfolioValue={effectivePortfolioValue}
            showMonetaryImpact={true}
            currency="CHF"
          />
        </div>

        {/* What-If Simulator */}
        <WhatIfSimulator
          onOpenComposer={() => {
            if (clientId) {
              setShowPortfolioComposer(true);
            } else {
              alert('Portfolio Composer is only available in client-specific view.');
            }
          }}
          currency="CHF"
        />
      </div>

      {/* Drawdown Analysis */}
      <DrawdownAnalysisSection
        underwaterData={underwaterData}
        significantDrawdowns={[]}
        currentDrawdown={performanceMetrics?.current_drawdown ?? null}
        maxDrawdown={performanceMetrics?.max_drawdown ?? null}
        avgDrawdown={0}
        defaultExpanded={false}
      />


      {/* Risk Recommendations */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Risk Management Recommendations</h3>
        <div className="space-y-3">
          {effectiveRiskMetrics.volatility > 20 && (
            <ActionCard
              type="urgent"
              severity="high"
              title="High Volatility"
              description="Consider reducing exposure to volatile assets or adding defensive positions."
              value={`${effectiveRiskMetrics.volatility.toFixed(2)}%`}
              actionLabel="Review Allocation"
              onAction={() => setShowPortfolioComposer(true)}
            />
          )}

          {Math.abs((effectiveRiskMetrics.beta || 0) - 1) > 0.5 && (
            <ActionCard
              type="performance"
              severity="medium"
              title={effectiveRiskMetrics.beta > 1.5 ? 'High Beta' : 'Low Beta'}
              description={`Portfolio is ${effectiveRiskMetrics.beta > 1.5 ? 'more' : 'less'} sensitive to market movements than average.`}
              value={effectiveRiskMetrics.beta.toFixed(2)}
              actionLabel="Adjust Beta"
              onAction={() => setShowPortfolioComposer(true)}
            />
          )}

          {effectiveRiskMetrics.sharpe_ratio != null && effectiveRiskMetrics.sharpe_ratio > 0 && effectiveRiskMetrics.sharpe_ratio < 0.5 && (
            <ActionCard
              type="urgent"
              severity="high"
              title="Low Sharpe Ratio"
              description="Risk-adjusted returns are below optimal. Consider rebalancing to improve efficiency."
              value={effectiveRiskMetrics.sharpe_ratio.toFixed(2)}
              actionLabel="Optimize Portfolio"
              onAction={() => setShowPortfolioComposer(true)}
            />
          )}

          {effectiveRiskMetrics.volatility <= 20 &&
           Math.abs((effectiveRiskMetrics.beta || 0) - 1) <= 0.5 &&
           (effectiveRiskMetrics.sharpe_ratio == null || effectiveRiskMetrics.sharpe_ratio >= 0.5) && (
            <ActionCard
              type="task"
              severity="low"
              title="Risk Metrics Within Range"
              description="All risk metrics are within acceptable parameters. Continue monitoring."
            />
          )}
        </div>
      </div>

      {/* Saved Proposals List */}
      {clientId && (
        <ProposalsList
          clientId={clientId}
          onSelectProposal={() => setShowPortfolioComposer(true)}
          refreshTrigger={proposalsRefreshTrigger}
        />
      )}

      {/* Portfolio Composer Modal */}
      {clientId && showPortfolioComposer && (
        <PortfolioComposer
          clientId={clientId}
          portfolioId={portfolioId}
          isOpen={showPortfolioComposer}
          onClose={() => setShowPortfolioComposer(false)}
          onSaveSuccess={() => {
            setShowPortfolioComposer(false);
            // Trigger refresh of proposals list
            setProposalsRefreshTrigger(prev => prev + 1);
          }}
        />
      )}
    </div>
  );
};

export default RiskTab;